import { Fragment, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { JournalEntryOwner, MapOwner } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';
import { StudioBestiary } from './StudioBestiary.js';
import { beatDisplayName, entryPlace, mapBeatId, type EntryPlace, type OwnerLookup } from './ownerPlace.js';
import { Pill } from '../ui/pill';
import SidebarPanel, { collapseSectionsIn } from '../ui/SidebarPanel';
import { PanelHeader } from '../ui/panel-header';
import { IconButton } from '../ui/icon-button';
import { ListRow, limitList, type RowDeco } from '../ui/row';
import { VisibilityToggle } from '../ui/visibility-toggle';
import { RowMenu, type MenuEntry } from '../ui/row-menu';
import { ArrowDown, ArrowUp, BookOpen, ChevronsDownUp, ExternalLink, FileText, ListChecks, Map as MapIcon, Pencil, Pin, Plus, Trash2, UserRound } from 'lucide-react';
import { useTreeOpen } from '../../hooks/useTreeOpen.js';
import { useAskDialogs } from './useAskDialogs.js';
import { BEAT_STATUS_KEY, forkWords, mainLine, planRows, type PlanOutcome, type PlanRow } from './beatPlan.js';
import { buildTreeOrder, visibleTreeRows } from '../../lib/page-tree.js';
import { AiMark, type AiMarkKind } from '../ai/AiMark.js';
import { searchFilter } from '../../lib/search/engine';

export type BeatStatus = 'draft' | 'ready' | 'played' | 'carried';

export interface Beat {
  readonly id: string;
  readonly order: number;
  readonly name?: string | null;
  readonly sessionLabel?: string | null;
  readonly status: BeatStatus;
  /** P04: the forks of the beat (GM-only data). */
  readonly outcomes?: readonly PlanOutcome[];
  /** P04: not null — a reserve beat of that beat: not numbered, drawn under its beat. */
  readonly reserveOf?: string | null;
}

export type SheetType = 'pc' | 'npc';

export interface CharacterSheet {
  readonly id: string;
  readonly name: string;
  readonly sheetType: SheetType;
  readonly group?: string;
  /** T-13: где лежит лист (бит энкаунтера). Нет = общий; в Библиотеке — только общие. */
  readonly holder?: MapOwner;
}

export type JournalEntryType =
  | 'npc'
  | 'location'
  | 'quest'
  | 'faction'
  | 'handout'
  | 'lore';

export interface JournalEntry {
  readonly id: string;
  readonly name: string;
  readonly type: JournalEntryType;
  /** P03: где лежит запись. Нет владельца = общая; в Библиотеке — только общие. */
  readonly owner?: JournalEntryOwner;
  /** Who sees it: the eye of the row (owner 30.10). Absent — the row has no eye. */
  readonly visibility?: 'gm' | 'all' | 'personal';
}

export type ReorderDirection = 'up' | 'down';

export type StudioEntityKind = 'sheet' | 'journal' | 'page';

export interface StudioEntityRef {
  readonly kind: StudioEntityKind;
  readonly id: string;
}

export type Translate = (key: keyof TranslationKeys) => string;

/** Минимум, который Библиотеке нужно знать о странице (карте). */
export interface StudioRailPage {
  readonly id: string;
  readonly name: string;
  /** P03: карта бита не в списке «Карты кампании». */
  readonly owner?: MapOwner;
  /** P06 (F-6): в какую карту вложена; «Карты кампании» — дерево. */
  readonly parentPageId?: string | null;
  readonly sortOrder?: number;
}

export interface StudioRailProps {
  readonly beats: readonly Beat[];
  readonly sheets: readonly CharacterSheet[];
  readonly journalEntries: readonly JournalEntry[];
  /** Страницы кампании — секция «Карты»; клик открывает канвас в фокусе
   * (карта сама дроп-цель, тащить её никуда не нужно). */
  readonly pages?: readonly StudioRailPage[];
  /** P03: цели квестов (id квеста → цели) — подпись «в цели «…»» вложенной заметки. */
  readonly questObjectives?: ReadonlyMap<string, ReadonlyArray<{ readonly id: string; readonly text: string }>>;
  readonly t: Translate;
  /** Выбранный бит — подсвечивается янтарной рамкой (макет: .beat.active). */
  readonly selectedBeatId?: string;
  readonly onSelectBeat: (beatId: string) => void;
  readonly onReorderBeat: (beatId: string, direction: ReorderDirection) => void;
  /** Одиночный клик = превью-таб; dblclick шлёт permanent:true (закрепить). */
  readonly onOpenEntity: (
    ref: StudioEntityRef,
    opts?: { readonly permanent?: boolean },
  ) => void;
  /** «＋ бит» — призрачная строка в конце плана (как в макете). */
  readonly onCreateBeat?: () => void;
  /** Создание бита в полёте — «＋ бит» ждёт ответа сервера (без дублей). */
  readonly creatingBeat?: boolean;
  /** Клик по статус-бейджу бита — цикл draft→ready→played→carried. */
  readonly onCycleStatus?: (beatId: string) => void;
  /**
   * Текущий игровой бит кампании (пакет 4, общий для всех GM) — отдельная
   * отметка «Сейчас»; выбор для редактирования (selectedBeatId) она не подменяет.
   */
  readonly currentBeatId?: string;
  /** Двойной клик по имени — inline-переименование (beat:update sessionLabel). */
  readonly onRenameBeat?: (beatId: string, label: string) => void;
  /** Сущности с pending-AI-черновиками — бейдж «черновик» в Библиотеке. */
  readonly draftEntityIds?: ReadonlySet<string>;
  /** AU-8: «Tia added» / «Tia changed» on beats and records the AI made or changed («beat:<id>», «journal:<id>»). */
  readonly aiMarks?: ReadonlyMap<string, AiMarkKind>;
  /** «＋» в заголовках секций — создание из Studio (Батч G). */
  readonly onCreateSheet?: (sheetType: SheetType) => void;
  readonly onCreatePage?: () => void;
  /**
   * «＋ Создать…» в заголовке Библиотеки — запись любого типа (выбор типа и
   * имени у хоста). Есть всегда, в том числе в пустой кампании, где групп с
   * «＋» ещё нет (GM Studio квесты 1).
   */
  readonly onCreateAnyEntry?: () => void;
  /** T-19: a group of sheets is renamed / deleted from its heading's ⋯ (every sheet of it moves). */
  readonly onSetSheetGroup?: (sheetId: string, group: string) => void;
  /** «Удалить лист…» in a sheet row's ⋯ (owner 31.10: an enemy made inside a beat had no way to be deleted from the rail). */
  readonly onDeleteSheet?: (sheetId: string) => void;
  /** «Замечания» (group E, P13): the item after the Library with its counter (0 — «none»). Absent at the table. */
  readonly remarks?: { readonly count: number; readonly active: boolean; readonly onOpen: () => void };
  /** The bestiary section with its «＋ на карту» wired to the session (the surface builds it); absent — the plain one. */
  readonly bestiary?: ReactNode;
  /** The eye of a Library record: shown → hide; hidden → the host asks «Показать игрокам?». Absent — no eye. */
  readonly onToggleEntryVisibility?: (entry: JournalEntry) => void;
}

export { BEAT_STATUS_KEY };

const JOURNAL_TYPE_KEY: Readonly<
  Record<JournalEntryType, keyof TranslationKeys>
> = {
  npc: 'journal.type.npc',
  location: 'journal.type.location',
  quest: 'journal.type.quest',
  faction: 'journal.type.faction',
  handout: 'journal.type.handout',
  lore: 'journal.type.lore',
};

export function beatLabel(beat: Beat, t: Translate): string {
  return beatDisplayName(beat, t);
}

export const StudioRail = (props: StudioRailProps): JSX.Element => {
  const {
    beats,
    sheets,
    journalEntries,
    pages = [],
    questObjectives,
    t,
    selectedBeatId,
    currentBeatId,
    onSelectBeat,
    onReorderBeat,
    onOpenEntity,
    onToggleEntryVisibility,
    onCreateBeat,
    creatingBeat = false,
    onCycleStatus,
    onRenameBeat,
    draftEntityIds,
    aiMarks,
    onCreateSheet,
    onCreatePage,
    onCreateAnyEntry,
    onSetSheetGroup,
    onDeleteSheet,
    remarks,
    bestiary,
  } = props;
  const { askName, askConfirm, dialogs: askDialogs } = useAskDialogs();

  // Поиск по Библиотеке — локальный фильтр по подстроке имени.
  const [query, setQuery] = useState<string>('');
  // Inline-переименование бита (двойной клик по имени).
  const [editingBeatId, setEditingBeatId] = useState<string | undefined>(undefined);
  const [editingValue, setEditingValue] = useState<string>('');
  const commitRename = (beatId: string): void => {
    const label = editingValue.trim();
    setEditingBeatId(undefined);
    if (label !== '') {
      onRenameBeat?.(beatId, label);
    }
  };
  const q = query.trim().toLowerCase();
  // P03: Библиотека показывает общее; заметки и карты битов/целей — за кнопкой
  // «Ещё в битах и квестах» и в поиске (с подписью хозяина).
  const lookup: OwnerLookup = {
    beatExists: (beatId: string): boolean => beats.some((b: Beat): boolean => b.id === beatId),
    quest: (questId: string) => {
      const quest = journalEntries.find((e: JournalEntry): boolean => e.id === questId && e.type === 'quest');
      return quest === undefined ? undefined : { objectives: questObjectives?.get(questId) ?? [] };
    },
  };
  const placeOf = (entry: JournalEntry): EntryPlace | null => entryPlace(entry.owner, lookup);
  const pageBeatId = (page: StudioRailPage): string | null => mapBeatId(page.owner, lookup);
  // P03 (Q-2): a beat's prep text is a nested record of its beat too — it is listed and counted with the notes.
  const nestedEntries: readonly JournalEntry[] = journalEntries.filter((e: JournalEntry): boolean => placeOf(e) !== null);
  const nestedPages: readonly StudioRailPage[] = pages.filter((p: StudioRailPage): boolean => pageBeatId(p) !== null);
  // T-13: a sheet held by a beat (a monster of its encounter) is nested like a beat's note.
  const sheetBeatId = (sheet: CharacterSheet): string | null => mapBeatId(sheet.holder, lookup);
  const nestedSheets: readonly CharacterSheet[] = sheets.filter((sh: CharacterSheet): boolean => sheetBeatId(sh) !== null);
  const sharedSheets: readonly CharacterSheet[] = sheets.filter((sh: CharacterSheet): boolean => sheetBeatId(sh) === null);
  const nestedCount: number = nestedEntries.length + nestedPages.length + nestedSheets.length;
  const sharedEntries: readonly JournalEntry[] = journalEntries.filter((e: JournalEntry): boolean => placeOf(e) === null);
  const sharedPages: readonly StudioRailPage[] = pages.filter((p: StudioRailPage): boolean => pageBeatId(p) === null);
  const beatName = (beatId: string): string => {
    const beat = beats.find((b: Beat): boolean => b.id === beatId);
    return beat === undefined ? '' : beatLabel(beat, t);
  };
  const questName = (questId: string): string => journalEntries.find((e: JournalEntry): boolean => e.id === questId)?.name ?? '';
  const fill = (key: keyof TranslationKeys, name: string): string => t(key).replace('{name}', name);
  // Подпись хозяина — в поиске, где вложенное лежит среди общего.
  const captionOfEntry = (entry: JournalEntry): string | undefined => {
    const place = placeOf(entry);
    if (place === null) return undefined;
    if (place.kind === 'beat') return fill('studio.owner.inBeat', beatName(place.beatId));
    if (place.kind === 'objective') return fill('studio.owner.inObjective', place.objectiveText);
    return fill('studio.owner.inQuest', questName(place.questId));
  };
  const captionOfPage = (page: StudioRailPage): string | undefined => {
    const beatId = pageBeatId(page);
    return beatId === null ? undefined : fill('studio.owner.inBeat', beatName(beatId));
  };
  const captionOfSheet = (sheet: CharacterSheet): string | undefined => {
    const beatId = sheetBeatId(sheet);
    return beatId === null ? undefined : fill('studio.owner.inBeat', beatName(beatId));
  };
  const searching = q !== '';
  // The app's one matcher (group M): typos, ё/е; while searching the best match comes first.
  const visibleSheets: readonly CharacterSheet[] =
    q === '' ? sharedSheets : searchFilter(query, sheets, (s) => [s.name]);
  const visibleEntries: readonly JournalEntry[] =
    q === '' ? sharedEntries : searchFilter(query, journalEntries, (e) => [e.name]);
  // P06: the tree of maps — a nested map is drawn under its parent, indented (a search shows a flat list).
  const treePos = (list: readonly StudioRailPage[]): StudioRailPage[] =>
    buildTreeOrder(list.map((p, i) => ({ ...p, parentPageId: p.parentPageId ?? null, sortOrder: p.sortOrder ?? i })));
  const visiblePages: readonly StudioRailPage[] =
    q === '' ? treePos(sharedPages) : searchFilter(query, pages, (p) => [p.name]);
  // The flat list: quests first, then the other record types; sheets follow. A search lists everything that matches, nested ones with their place.
  const LIB_ORDER: readonly JournalEntryType[] = ['quest', 'npc', 'location', 'faction', 'handout', 'lore'];
  const libraryEntries: readonly JournalEntry[] = searching
    ? visibleEntries
    : [...visibleEntries].sort((x: JournalEntry, y: JournalEntry): number => LIB_ORDER.indexOf(x.type) - LIB_ORDER.indexOf(y.type));
  const ungroupedSheets: readonly CharacterSheet[] = visibleSheets.filter((sh: CharacterSheet): boolean => sh.group === undefined || sh.group === '');
  const groupNames: readonly string[] = [...new Set(visibleSheets.map((sh: CharacterSheet): string => sh.group ?? '').filter((g) => g !== ''))].sort();
  const sharedCount: number = sharedEntries.length + sharedSheets.length;
  // «Ещё в битах и квестах»: вложенное по хозяевам — сначала биты по порядку, потом квесты.
  interface NestedGroup { readonly key: string; readonly label: string; readonly entries: JournalEntry[]; readonly pages: StudioRailPage[]; readonly sheets: CharacterSheet[]; }
  const nestedGroups: NestedGroup[] = [];
  const groupFor = (key: string, label: string): NestedGroup => {
    const found = nestedGroups.find((g) => g.key === key);
    if (found !== undefined) return found;
    const created: NestedGroup = { key, label, entries: [], pages: [], sheets: [] };
    nestedGroups.push(created);
    return created;
  };
  for (const beat of [...beats].sort((x: Beat, y: Beat): number => x.order - y.order)) {
    const label = fill('studio.rail.nestedBeat', beatLabel(beat, t));
    const beatEntries = nestedEntries.filter((e) => {
      const place = placeOf(e);
      return place !== null && place.kind === 'beat' && place.beatId === beat.id;
    });
    const beatPages = nestedPages.filter((pg) => pageBeatId(pg) === beat.id);
    const beatSheets = nestedSheets.filter((sh) => sheetBeatId(sh) === beat.id);
    if (beatEntries.length + beatPages.length + beatSheets.length === 0) continue;
    const g = groupFor(`beat:${beat.id}`, label);
    g.entries.push(...beatEntries);
    g.pages.push(...beatPages);
    g.sheets.push(...beatSheets);
  }
  for (const quest of journalEntries.filter((e: JournalEntry): boolean => e.type === 'quest')) {
    const questEntries = nestedEntries.filter((e) => {
      const place = placeOf(e);
      return place !== null && place.kind !== 'beat' && place.questId === quest.id;
    });
    if (questEntries.length === 0) continue;
    groupFor(`quest:${quest.id}`, fill('studio.rail.nestedQuest', quest.name)).entries.push(...questEntries);
  }

  // P04: the main line is numbered and can be reordered; a reserve beat hangs under its beat.
  const orderedBeats: readonly Beat[] = mainLine(beats);
  const planOrder: readonly PlanRow<Beat>[] = planRows(beats);

  // ── sidebar polish (brief П0–П9, mockup prepRail()) ──
  const navRef = useRef<HTMLElement>(null);
  const treeRef = useRef<HTMLUListElement>(null);
  const [chip, setChip] = useState<string>('all');
  const [more, setMore] = useState<ReadonlySet<string>>(new Set());
  const setMoreKey = (key: string, open: boolean): void =>
    setMore((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  const tree = useTreeOpen(undefined, pages.map((p) => ({ id: p.id, parentPageId: p.parentPageId ?? null })), treeRef, true);
  const want = (k: string): boolean => chip === 'all' || chip === k;

  /** One row of the rail (П2): the draggable/clickable name keeps the rail's own button (`studio-rail__entity`). */
  const railRow = (key: string, name: JSX.Element, extra: { meta?: ReactNode; state?: ReactNode; deco?: RowDeco[]; menu?: MenuEntry[]; depth?: number; twisty?: { open: boolean; onToggle: () => void } | null; testid?: string } = {}): JSX.Element => (
    <ListRow
      key={key}
      label=""
      labelSlot={name}
      meta={extra.meta}
      state={extra.state}
      deco={extra.deco}
      menu={extra.menu}
      depth={extra.depth}
      twisty={extra.twisty}
      data-testid={extra.testid}
    />
  );
  const NAME = 'studio-rail__entity list-row__name';

  const entryRow = (entry: JournalEntry, caption: string | undefined, kind?: string): JSX.Element =>
    railRow(
      entry.id,
      <button
        type="button"
        className={`${NAME} cursor-grab active:cursor-grabbing`}
        title={caption !== undefined ? `${entry.name} — ${caption}` : entry.name}
        draggable
        onDragStart={(e): void => {
          // Any journal entry (locations included) drops onto the studio map as a pin:
          // CanvasArea → drop:journal-entry → PinManager (previewed page).
          e.dataTransfer.setData('text/plain', entry.name);
          e.dataTransfer.setData('application/json', JSON.stringify({ dragType: 'journal-entry', entryId: entry.id, entryName: entry.name }));
          e.dataTransfer.effectAllowed = 'copyMove';
        }}
        onClick={(): void => onOpenEntity({ kind: 'journal', id: entry.id })}
        onDoubleClick={(): void => onOpenEntity({ kind: 'journal', id: entry.id }, { permanent: true })}
      >
        {entry.name}
      </button>,
      {
        meta: (
          <>
            {caption !== undefined ? <span className="studio-rail__owner-caption">{caption}</span> : null}
            {kind !== undefined ? <span className="studio-rail__kind">{kind}</span> : null}
          </>
        ),
        // Owner 30.10: the same eye as the table's Journal — Eye «видят игроки», EyeOff (warn) «скрыто».
        state:
          onToggleEntryVisibility !== undefined && (entry.visibility === 'gm' || entry.visibility === 'all') ? (
            <VisibilityToggle shown={entry.visibility === 'all'} testid="rail-entry-eye" onToggle={(): void => onToggleEntryVisibility(entry)} />
          ) : undefined,
        deco: [
          ...(aiMarks?.get(`journal:${entry.id}`) !== undefined ? [{ icon: <AiMark kind={aiMarks.get(`journal:${entry.id}`)!} />, label: t('studio.rail.draftBadge') }] : []),
          ...(draftEntityIds?.has(entry.id) ? [{ icon: <span className="studio-rail__draft-badge rounded border border-dashed border-draft/60 px-1 text-label text-draft">{t('studio.rail.draftBadge')}</span>, label: t('studio.rail.draftBadge') }] : []),
        ],
        menu: [
          { label: t('sb.journal.open'), icon: BookOpen, onSelect: () => onOpenEntity({ kind: 'journal', id: entry.id }) },
          { label: t('sb.rail.pin'), icon: Pin, onSelect: () => onOpenEntity({ kind: 'journal', id: entry.id }, { permanent: true }) },
        ],
      },
    );

  const sheetRow = (sheet: CharacterSheet, caption: string | undefined, kind?: string): JSX.Element =>
    railRow(
      sheet.id,
      <button
        type="button"
        className={`${NAME} cursor-grab active:cursor-grabbing`}
        title={caption !== undefined ? `${sheet.name} — ${caption}` : sheet.name}
        draggable
        onDragStart={(e): void => {
          // Same payload CanvasArea.handleDrop expects; dropping on the studio map spawns a token bound to this sheet.
          e.dataTransfer.setData('text/plain', sheet.name);
          e.dataTransfer.setData('application/json', JSON.stringify({ dragType: 'character-sheet', sheetId: sheet.id, sheetName: sheet.name }));
          e.dataTransfer.effectAllowed = 'copyMove';
        }}
        onClick={(): void => onOpenEntity({ kind: 'sheet', id: sheet.id })}
        onDoubleClick={(): void => onOpenEntity({ kind: 'sheet', id: sheet.id }, { permanent: true })}
      >
        {sheet.name}
      </button>,
      {
        meta: (
          <>
            {caption !== undefined ? <span className="studio-rail__owner-caption">{caption}</span> : null}
            {kind !== undefined ? <span className="studio-rail__kind">{kind}</span> : null}
          </>
        ),
        deco: draftEntityIds?.has(sheet.id) ? [{ icon: <span className="studio-rail__draft-badge rounded border border-dashed border-draft/60 px-1 text-label text-draft">{t('studio.rail.draftBadge')}</span>, label: t('studio.rail.draftBadge') }] : undefined,
        menu: [
          { label: t('sb.sheet.open'), icon: FileText, onSelect: () => onOpenEntity({ kind: 'sheet', id: sheet.id }) },
          { label: t('sb.rail.pin'), icon: Pin, onSelect: () => onOpenEntity({ kind: 'sheet', id: sheet.id }, { permanent: true }) },
          ...(onDeleteSheet !== undefined ? [{
            label: t('studio.sheet.delete'),
            icon: Trash2,
            danger: true,
            testid: 'studio-sheet-delete',
            onSelect: (): void => {
              void askConfirm(t('characters.deleteConfirmDesc').replace('{name}', sheet.name), '', { tone: 'danger' }).then((yes): void => {
                if (yes) onDeleteSheet(sheet.id);
              });
            },
          }] : []),
        ],
      },
    );

  const pageRow = (page: StudioRailPage, caption: string | undefined, opts: { depth?: number; twisty?: { open: boolean; onToggle: () => void } | null; meta?: string } = {}): JSX.Element =>
    railRow(
      page.id,
      <button
        type="button"
        className={NAME}
        title={caption !== undefined ? `${page.name} — ${caption}` : page.name}
        data-depth={opts.depth ?? 0}
        onClick={(): void => onOpenEntity({ kind: 'page', id: page.id })}
        onDoubleClick={(): void => onOpenEntity({ kind: 'page', id: page.id }, { permanent: true })}
      >
        {page.name}
      </button>,
      {
        meta: caption !== undefined ? <span className="studio-rail__owner-caption">{caption}</span> : opts.meta,
        depth: opts.depth,
        twisty: opts.twisty,
        menu: [
          { label: t('sb.journal.open'), icon: MapIcon, onSelect: () => onOpenEntity({ kind: 'page', id: page.id }) },
          { label: t('sb.rail.pin'), icon: Pin, onSelect: () => onOpenEntity({ kind: 'page', id: page.id }, { permanent: true }) },
          { separator: true },
          { note: t('map.row.studioNote') },
        ],
      },
    );

  /** A list with «Показать ещё N» after 12 (П8); the whole list while searching. */
  const limited = <T,>(key: string, items: readonly T[], render: (item: T) => JSX.Element): JSX.Element => {
    const { shown, more: moreButton } = limitList(key, items, more, setMoreKey, searching);
    return (
      <>
        <ul className="flex flex-col">{shown.map(render)}</ul>
        {moreButton}
      </>
    );
  };

  const createItems: MenuEntry[] = [
    { label: t('sb.rail.createBeat'), icon: ListChecks, onSelect: () => onCreateBeat?.(), hidden: onCreateBeat === undefined, disabled: creatingBeat },
    { label: t('sb.rail.createEntry'), icon: BookOpen, onSelect: () => onCreateAnyEntry?.(), hidden: onCreateAnyEntry === undefined },
    { label: t('sb.rail.createMap'), icon: MapIcon, onSelect: () => onCreatePage?.(), hidden: onCreatePage === undefined },
    { label: t('sb.rail.createPc'), icon: UserRound, onSelect: () => onCreateSheet?.('pc'), hidden: onCreateSheet === undefined },
    { label: t('sb.rail.createNpc'), icon: FileText, onSelect: () => onCreateSheet?.('npc'), hidden: onCreateSheet === undefined },
  ];
  const typeGroups: readonly JournalEntryType[] = ['quest', 'npc', 'location', 'faction', 'lore', 'handout'];
  const TYPE_TITLE: Record<JournalEntryType, keyof TranslationKeys> = {
    quest: 'sb.type.quest', npc: 'sb.type.npc', location: 'sb.type.location', faction: 'sb.type.faction', lore: 'sb.type.lore', handout: 'sb.type.handout',
  };
  const chips = [
    { id: 'all', label: t('sb.chip.all') },
    ...typeGroups.filter((ty) => sharedEntries.some((e) => e.type === ty)).map((ty) => ({ id: ty, label: t(TYPE_TITLE[ty]), count: sharedEntries.filter((e) => e.type === ty).length })),
    ...(sharedSheets.length > 0 ? [{ id: 'sheets', label: t('sb.rail.sheets'), count: sharedSheets.length }] : []),
  ];
  const sharedRoots = treePos(sharedPages).filter((p) => p.parentPageId === null || p.parentPageId === undefined || !sharedPages.some((x) => x.id === p.parentPageId));
  const treeRows = visibleTreeRows(
    treePos(sharedPages).map((p) => ({ ...p, parentPageId: p.parentPageId ?? null, sortOrder: p.sortOrder ?? 0 })),
    sharedRoots.map((p) => ({ ...p, parentPageId: p.parentPageId ?? null, sortOrder: p.sortOrder ?? 0 })),
    tree.isOpen,
  );
  const beatNumber = (beatId: string | null): string => {
    const row = planOrder.find((r) => r.beat.id === beatId);
    return row?.number != null ? t('sb.rail.beatN').replace('{n}', String(row.number)) : beatName(beatId ?? '');
  };

  return (
    <nav ref={navRef} className="studio-rail flex flex-col text-ui" aria-label={t('studio.rail.plan')}>
      <PanelHeader
        title={t('sb.rail.title')}
        actions={
          <>
            <RowMenu items={createItems} label={t('sb.rail.create')} size="md" icon={<Plus size={14} aria-hidden="true" />} testid="studio-rail-create" />
            <IconButton label={t('sb.rail.collapseAll')} icon={<ChevronsDownUp size={14} aria-hidden="true" />} onClick={(): void => collapseSectionsIn(navRef.current, 'sections')} />
          </>
        }
      />

      <SidebarPanel
        id="rail.plan"
        title={t('studio.rail.plan')}
        count={beats.length}
        tight
        testid="studio-rail-plan"
        actions={
          onCreateBeat !== undefined ? (
            <IconButton label={t('sb.rail.addBeat')} icon={<Plus size={14} aria-hidden="true" />} data-testid="studio-create-beat" disabled={creatingBeat} onClick={onCreateBeat} />
          ) : undefined
        }
      >
        <section className="studio-rail__plan">
          <ol className="studio-rail__beats flex flex-col">
            {planOrder.flatMap((row: PlanRow<Beat>): JSX.Element[] => {
              const beat: Beat = row.beat;
              const reserveRow: boolean = row.number === null;
              const index: number = row.number === null ? -1 : row.number - 1;
              const selected = beat.id === selectedBeatId;
              const items: JSX.Element[] = [];
              if (row.forkLabel !== undefined) {
                items.push(
                  <li key={`fork:${beat.id}`} role="presentation" className="studio-rail__fork list-none text-label text-dim">
                    {t('beat.reserve.railCaption').replace('{label}', forkWords(row.forkLabel))}
                  </li>,
                );
              }
              const order = (
                <span
                  className={`studio-rail__beat-order w-4 flex-none text-pill tabular-nums ${
                    reserveRow ? 'text-reserve' : beat.status === 'played' ? 'text-ok' : selected ? 'text-hl' : 'text-dim'
                  }`}
                >
                  {reserveRow ? '↳' : beat.status === 'played' ? '✓' : row.number}
                </span>
              );
              items.push(
                <ListRow
                  key={beat.id}
                  className={`studio-rail__beat ${reserveRow ? 'studio-rail__beat--reserve ' : ''}${selected ? 'studio-rail__beat--active' : ''}`}
                  current={selected}
                  data-selected={selected ? 'true' : undefined}
                  depth={reserveRow ? 1 : 0}
                  label=""
                  labelSlot={
                    editingBeatId === beat.id ? (
                      // Rename: the input replaces the row's name (never inside a button).
                      <span className="studio-rail__beat-select flex min-w-0 flex-1 items-center gap-1.5">
                        {order}
                        <input
                          autoFocus
                          type="text"
                          className="studio-rail__beat-rename min-w-0 flex-1 rounded border border-hl/50 bg-surface px-1 text-body-s text-fg focus:outline-none"
                          value={editingValue}
                          onChange={(e): void => setEditingValue(e.target.value)}
                          onBlur={(): void => commitRename(beat.id)}
                          onKeyDown={(e): void => {
                            if (e.key === 'Enter') commitRename(beat.id);
                            if (e.key === 'Escape') setEditingBeatId(undefined);
                          }}
                        />
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="studio-rail__beat-select list-row__name flex min-w-0 flex-1 items-center gap-1.5"
                        title={onRenameBeat !== undefined ? `${beatLabel(beat, t)} — ${t('studio.beat.rename')}` : beatLabel(beat, t)}
                        onClick={(): void => onSelectBeat(beat.id)}
                        onDoubleClick={(): void => {
                          if (onRenameBeat === undefined) return;
                          setEditingBeatId(beat.id);
                          setEditingValue(beat.name ?? beat.sessionLabel ?? '');
                        }}
                      >
                        {order}
                        <span
                          className={`studio-rail__beat-name min-w-0 truncate ${
                            beat.status === 'played' ? 'text-dim line-through' : reserveRow ? 'text-reserve' : 'text-fg'
                          }`}
                        >
                          {beatLabel(beat, t)}
                        </span>
                        {aiMarks?.get(`beat:${beat.id}`) !== undefined ? <AiMark kind={aiMarks.get(`beat:${beat.id}`)!} /> : null}
                      </button>
                    )
                  }
                  pills={
                    <>
                      {reserveRow && beat.id !== currentBeatId ? (
                        <Pill tone="reserve" className="studio-rail__beat-reserve flex-none" data-testid="studio-rail-beat-reserve">{t('beat.reserve.pill')}</Pill>
                      ) : null}
                      {beat.id === currentBeatId ? (
                        <Pill tone="ok" className="studio-rail__beat-current flex-none" data-testid="studio-rail-beat-current">{t('plan.list.now')}</Pill>
                      ) : null}
                    </>
                  }
                  meta={
                    reserveRow && beat.status === 'draft' ? undefined : (
                      // The status is plain text (the one pill of a row is «now» or «reserve»); a click cycles it.
                      <button
                        type="button"
                        className={`studio-rail__beat-status cursor-pointer rounded px-0.5 hover:text-hl ${beat.status === 'carried' ? 'text-hl' : ''}`}
                        data-status={beat.status}
                        title={t('studio.beat.cycleStatus')}
                        onClick={(): void => onCycleStatus?.(beat.id)}
                      >
                        {beat.status === 'carried' ? '→ ' : ''}
                        {t(BEAT_STATUS_KEY[beat.status])}
                      </button>
                    )
                  }
                  menu={[
                    {
                      label: t('sb.rail.rename'),
                      icon: Pencil,
                      hidden: onRenameBeat === undefined,
                      onSelect: () => {
                        setEditingBeatId(beat.id);
                        setEditingValue(beat.name ?? beat.sessionLabel ?? '');
                      },
                    },
                    { label: t('sb.rail.up'), icon: ArrowUp, hidden: reserveRow, disabled: index === 0, onSelect: () => onReorderBeat(beat.id, 'up'), testid: 'studio-rail-beat-up' },
                    { label: t('sb.rail.down'), icon: ArrowDown, hidden: reserveRow, disabled: index === orderedBeats.length - 1, onSelect: () => onReorderBeat(beat.id, 'down'), testid: 'studio-rail-beat-down' },
                  ]}
                />,
              );
              return items;
            })}
          </ol>
        </section>
      </SidebarPanel>

      <div className="studio-rail__library">
        <SidebarPanel
          id="rail.lib"
          title={t('studio.rail.library')}
          count={sharedCount}
          info={`${t('studio.library.noteLead').replace('{shared}', String(sharedCount)).replace('{nested}', String(nestedCount))}. ${t('studio.library.noteFine')}`}
          tight
          testid="studio-rail-library"
          actions={
            onCreateAnyEntry !== undefined ? (
              <IconButton label={t('sb.rail.createEntry')} icon={<Plus size={14} aria-hidden="true" />} data-testid="studio-library-create" onClick={onCreateAnyEntry} />
            ) : undefined
          }
        >
          {/* П8: the search (it lists the nested things too, with their place) and the type chips */}
          <div className="list-find list-find--flush">
            <input
              type="search"
              className="studio-rail__search list-find__input"
              placeholder={t('studio.rail.search')}
              aria-label={t('studio.rail.search')}
              value={query}
              onChange={(e): void => setQuery(e.target.value)}
            />
            {chips.length > 2 && !searching ? (
              <div className="list-find__chips" role="group" aria-label={t('sb.filter')}>
                {chips.map((c) => (
                  <button key={c.id} type="button" className="list-chip" aria-pressed={chip === c.id} onClick={(): void => setChip(c.id)}>
                    {c.label}
                    {c.count !== undefined ? <span className="list-chip__n">{c.count}</span> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <span className="sr-only" data-testid="studio-rail-lib-note">
            {t('studio.library.noteLead').replace('{shared}', String(sharedCount)).replace('{nested}', String(nestedCount))}
          </span>

          {sheets.length === 0 && journalEntries.length === 0 && pages.length === 0 ? (
            <p className="studio-rail__library-empty sb-line py-2 text-meta leading-relaxed text-dim">{t('studio.library.empty')}</p>
          ) : null}

          <div data-testid="studio-library-list">
            {searching ? (
              // A search: one list of everything that matches, nested ones with their place.
              <ul className="flex flex-col">
                {libraryEntries.map((entry) => entryRow(entry, captionOfEntry(entry), t(JOURNAL_TYPE_KEY[entry.type])))}
                {visibleSheets.map((sheet) => sheetRow(sheet, captionOfSheet(sheet), t(sheet.sheetType === 'pc' ? 'studio.rail.kindPc' : 'studio.rail.kindNpcSheet')))}
              </ul>
            ) : (
              <>
                {typeGroups.map((ty) => {
                  const list = libraryEntries.filter((e) => e.type === ty);
                  if (list.length === 0 || !want(ty)) return null;
                  return (
                    <SidebarPanel key={ty} id={`rail.lib.${ty}`} title={t(TYPE_TITLE[ty])} count={list.length} tight level="sub" testid={`studio-rail-type-${ty}`}>
                      {limited(`lib.${ty}`, list, (entry) => entryRow(entry, undefined, t(JOURNAL_TYPE_KEY[entry.type])))}
                    </SidebarPanel>
                  );
                })}
                {want('sheets') && (visibleSheets.length > 0 || onCreateSheet !== undefined) ? (
                  <SidebarPanel
                    id="rail.lib.sheets"
                    title={t('sb.rail.sheets')}
                    count={visibleSheets.length}
                    tight
                    level="sub"
                    testid="studio-rail-sheets"
                    actions={
                      onCreateSheet !== undefined ? (
                        <>
                          <IconButton size="sm" label={t('studio.rail.createSheetPc')} icon={<UserRound size={14} aria-hidden="true" />} data-testid="studio-create-sheet-pc" onClick={(): void => onCreateSheet('pc')} />
                          <IconButton size="sm" label={t('studio.rail.createSheetNpc')} icon={<FileText size={14} aria-hidden="true" />} data-testid="studio-create-sheet-npc" onClick={(): void => onCreateSheet('npc')} />
                        </>
                      ) : undefined
                    }
                  >
                    <ul className="flex flex-col">
                      {ungroupedSheets.map((sheet) => sheetRow(sheet, undefined, t(sheet.sheetType === 'pc' ? 'studio.rail.kindPc' : 'studio.rail.kindNpcSheet')))}
                      {groupNames.map((g) => (
                        <Fragment key={`group:${g}`}>
                          {/* a folder (D-36 «Папка», the GM's): its ⋯ renames it or takes the sheets out of it */}
                          <li className="studio-rail__sheet-group sb-line mt-1 flex list-none items-center gap-1 text-meta font-semibold text-muted-foreground" data-sheet-group={g}>
                            <span className="min-w-0 flex-1 truncate" title={g}>{g}</span>
                            {onSetSheetGroup !== undefined ? (
                              <RowMenu
                                label={t('studio.rail.groupMenu')}
                                testid="studio-sheet-group-menu"
                                items={[
                                  {
                                    label: t('studio.rail.groupRename'),
                                    icon: Pencil,
                                    testid: 'studio-sheet-group-rename',
                                    onSelect: () => {
                                      void askName(t('studio.sheet.renameGroupPrompt'), g, { maxLength: 50 }).then((next): void => {
                                        if (next === null || next === g) return;
                                        for (const sh of sheets) if (sh.group === g) onSetSheetGroup(sh.id, next);
                                      });
                                    },
                                  },
                                  {
                                    label: t('studio.rail.groupDelete'),
                                    icon: Trash2,
                                    danger: true,
                                    testid: 'studio-sheet-group-delete',
                                    onSelect: () => {
                                      void askConfirm(t('studio.sheet.deleteGroupConfirm').replace('{name}', g), '', { tone: 'danger' }).then((yes): void => {
                                        if (!yes) return;
                                        for (const sh of sheets) if (sh.group === g) onSetSheetGroup(sh.id, '');
                                      });
                                    },
                                  },
                                ]}
                              />
                            ) : null}
                          </li>
                          {visibleSheets.filter((sh) => sh.group === g).map((sheet) => sheetRow(sheet, undefined, t(sheet.sheetType === 'pc' ? 'studio.rail.kindPc' : 'studio.rail.kindNpcSheet')))}
                        </Fragment>
                      ))}
                    </ul>
                  </SidebarPanel>
                ) : null}
                {chip === 'all' && nestedCount > 0 ? (
                  // П7: what was made inside a beat or an objective lives there — the last group, folded.
                  <SidebarPanel
                    id="rail.lib.nested"
                    title={t('sb.nested')}
                    count={nestedCount}
                    info={t('studio.library.noteFine')}
                    tight
                    level="sub"
                    defaultCollapsed
                    testid="studio-rail-nested"
                  >
                    {nestedGroups.map((group: NestedGroup): JSX.Element => (
                      <div key={group.key} className="studio-rail__nested-group flex flex-col" data-nested-group={group.key}>
                        <h4 className="studio-rail__group-label sb-line pt-1 text-pill text-dim">{group.label}</h4>
                        <ul className="studio-rail__nested-list flex flex-col">
                          {group.entries.map((entry: JournalEntry): JSX.Element => {
                            const place = placeOf(entry);
                            return entryRow(entry, place?.kind === 'objective' ? fill('studio.owner.inObjective', place.objectiveText) : undefined);
                          })}
                          {group.pages.map((page: StudioRailPage): JSX.Element => pageRow(page, undefined))}
                          {group.sheets.map((sheet: CharacterSheet): JSX.Element => sheetRow(sheet, undefined))}
                        </ul>
                      </div>
                    ))}
                  </SidebarPanel>
                ) : null}
              </>
            )}
          </div>
        </SidebarPanel>

        {visiblePages.length > 0 || onCreatePage !== undefined ? (
          <SidebarPanel
            id="rail.maps"
            title={t('studio.rail.maps')}
            count={pages.length}
            tight
            testid="studio-rail-maps"
            actions={
              <>
                {onCreatePage !== undefined ? (
                  <IconButton label={t('sb.rail.newMap')} className="studio-rail__create" icon={<Plus size={14} aria-hidden="true" />} onClick={onCreatePage} />
                ) : null}
                <IconButton label={t('sb.collapseAll')} icon={<ChevronsDownUp size={14} aria-hidden="true" />} onClick={(): void => collapseSectionsIn(treeRef.current, 'tree')} />
              </>
            }
          >
            <div className="studio-rail__pages-group flex flex-col">
              <ul className="studio-rail__pages-list flex flex-col" ref={treeRef}>
                {searching
                  ? visiblePages.map((page) => pageRow(page, captionOfPage(page)))
                  : treeRows.map((r) =>
                      pageRow(r.page, undefined, {
                        depth: r.depth,
                        twisty: r.open !== null ? { open: r.open, onToggle: (): void => tree.toggle(r.page.id) } : r.column ? null : undefined,
                      }),
                    )}
              </ul>
              {!searching && nestedPages.length > 0 ? (
                <SidebarPanel id="rail.maps.owned" title={t('sb.rail.onlyInBeats')} count={nestedPages.length} tight level="sub" defaultCollapsed testid="studio-rail-maps-hidden">
                  <ul className="flex flex-col">{nestedPages.map((page) => pageRow(page, undefined, { meta: beatNumber(pageBeatId(page)) }))}</ul>
                </SidebarPanel>
              ) : null}
            </div>
          </SidebarPanel>
        ) : null}

        <SidebarPanel id="rail.best" title={t('studio.rail.bestiary')} tight defaultCollapsed testid="studio-rail-bestiary">
          {bestiary ?? <StudioBestiary t={t} embedded />}
        </SidebarPanel>
      </div>
      {askDialogs}
      {remarks === undefined ? null : (
        // П1/П7: a section like the others, folded; the count (warn) in its header, and the one action of the section —
        // «Открыть замечания» — an icon of the header (one click, as the old item); unfolded it says it in words too.
        <SidebarPanel
          id="rail.rem"
          title={t('remarks.rail')}
          // not a named region: the remarks panel in the main area is «the» region «Замечания»
          regionLabel=""
          // the count in a warn pill; none — said in words (as the old item: «нет»)
          count={remarks.count > 0 ? remarks.count : t('remarks.none')}
          countTone={remarks.count > 0 ? 'warn' : 'plain'}
          countTestid={remarks.count > 0 ? 'remarks-count' : undefined}
          defaultCollapsed
          testid="studio-rail-remarks"
          actions={
            <IconButton
              label={t('sb.rail.openRemarks')}
              icon={<ExternalLink size={14} aria-hidden="true" />}
              className={remarks.active ? 'text-hl' : undefined}
              aria-current={remarks.active ? 'page' : undefined}
              data-testid="studio-rail-remarks-open"
              onClick={remarks.onOpen}
            />
          }
        >
          <button
            type="button"
            className={`studio-rail__remarks flex w-full items-center gap-2 rounded-md border py-1.5 text-left text-body-s ${remarks.active ? 'border-hl/45 bg-hl/10 text-fg' : 'border-line text-fg hover:bg-panel-2 hover:text-fg'}`}
            aria-current={remarks.active ? 'page' : undefined}
            onClick={remarks.onOpen}
          >
            <span className="min-w-0 flex-1 truncate">{t('sb.rail.openRemarks')}</span>
            {remarks.count === 0 ? <span className="flex-none text-pill text-dim">{t('remarks.none')}</span> : null}
          </button>
        </SidebarPanel>
      )}
    </nav>
  );
};
